
import { DeliveryProvider, SystemConfig } from '../types';

const extractJSON = (text: string) => {
  try {
    const firstBrace = text.indexOf('{');
    const lastBrace = text.lastIndexOf('}');
    if (firstBrace === -1 || lastBrace === -1) throw new Error("JSON não encontrado");
    return JSON.parse(text.substring(firstBrace, lastBrace + 1));
  } catch (e) {
    throw new Error("A IA retornou um formato ilegível.");
  }
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = error => reject(error);
  });
};

export const parseDeliveryTableFile = async (file: File, systemConfig: SystemConfig): Promise<DeliveryProvider> => {
  const apiKey = systemConfig.geminiApiKey;

  if (!apiKey) {
    throw new Error("Chave Gemini não configurada.");
  }

  try {
    const base64Data = await fileToBase64(file);

    const prompt = `
      Você é um especialista em logística. Analise esta imagem de tabela de preços de entrega.
      
      OBJETIVOS:
      1. Extraia o nome da empresa (procure logotipos ou títulos grandes como "Gâmi", "Uber", etc).
      2. Identifique a estratégia:
         - "tiered": se houver faixas de KM (ex: 0-1km: R$7,79).
         - "per_km": se houver taxa base + valor por km.
         - "flat": se for um único valor.
      3. Extraia TODAS as faixas de preço se for 'tiered'.
      
      REGRAS DE JSON:
      - 'name': string com o nome da empresa.
      - 'strategy': string 'tiered', 'per_km' ou 'flat'.
      - 'baseFee': número decimal (taxa inicial).
      - 'pricePerKm': número decimal.
      - 'tiers': array de objetos { "maxDistance": número, "price": número }. 
        Ex: "2 A 3 KM" -> maxDistance: 3.
      
      Retorne APENAS o JSON estruturado.
    `;

    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${apiKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{
          parts: [
            { text: prompt },
            { inline_data: { mime_type: file.type, data: base64Data } }
          ]
        }],
        generationConfig: { response_mime_type: "application/json" }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || "Erro na API.");
    }

    const data = await response.json();
    const parsedData = extractJSON(data.candidates?.[0]?.content?.parts?.[0]?.text || "{}");

    // Normalização rigorosa
    return {
      id: Math.random().toString(36).substr(2, 5),
      name: parsedData.name || parsedData.nome || "Parceiro Importado",
      color: '#3498DB',
      strategy: (parsedData.strategy || 'tiered') as any,
      baseFee: Number(parsedData.baseFee) || Number(parsedData.taxa_base) || 0,
      pricePerKm: Number(parsedData.pricePerKm) || Number(parsedData.preco_km) || 0,
      tiers: (parsedData.tiers || parsedData.faixas || []).map((t: any) => ({
        maxDistance: Number(t.maxDistance || t.distancia_maxima || t.km || 0),
        price: Number(t.price || t.preco || t.valor || 0)
      })).filter((t: any) => t.maxDistance > 0 && t.price > 0),
      active: true,
      priority: 99 // v56.11: Default priority for imported providers
    };

  } catch (error: any) {
    console.error("Erro no Processador de Imagem:", error);
    throw new Error(`Falha ao ler tabela: ${error.message}`);
  }
};
